import { CompanyWebFigureNote } from "@/components/company/company-web-figures";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useApi } from "@/hooks/useApi";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { useTranslation } from "@/hooks/useTranslation";
import { webFigureText } from "@/lib/company-web-figures";
import { getInitialsFromParts } from "@/lib/initials";
import { toSearchableString } from "@/lib/string";
import { cn } from "@/lib/utils";
import {
  CompanyDTO,
  CompanyExecutive,
  CompanyWebFigure,
  displayableLegalForm,
  EmployeeDTO,
  EmployeeSearchResponse,
  filterDirectors,
  directorUsualName,
  findCompanyHead,
} from "@shaarp/common";
import {
  BuildingIcon,
  CalendarIcon,
  ChevronDownIcon,
  LineChartIcon,
  ScaleIcon,
} from "lucide-react";
import { type ReactNode, useEffect, useEffectEvent, useState } from "react";
import { RiLinkedinFill } from "react-icons/ri";

const ENTERPRISE_CATEGORY_FULL_NAMES: Record<string, string> = {
  TPE: "Très Petite Entreprise",
  PME: "Petite à Moyenne Entreprise",
  ETI: "Entreprise de Taille Intermédiaire",
  GE: "Grande Entreprise",
};

type MetricRowProps = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: ReactNode;
  showBorder?: boolean;
};

function MetricRow({
  icon: Icon,
  label,
  value,
  showBorder = true,
}: MetricRowProps) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-4 py-3",
        showBorder && "border-t",
      )}
    >
      <div className="flex items-center gap-3 text-sm shrink-0">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <span className="text-muted-foreground">{label}</span>
      </div>
      <span className="text-sm font-semibold text-right">{value}</span>
    </div>
  );
}

type ExecutiveRowProps = {
  executive: CompanyExecutive;
  employee?: EmployeeDTO | null;
  featured?: boolean;
};

function ExecutiveRow({
  executive,
  employee,
  featured = false,
}: ExecutiveRowProps) {
  const initials = getInitialsFromParts(
    executive.firstName,
    executive.lastName,
  );

  return (
    <div className={cn("flex items-center", featured ? "gap-4" : "gap-3")}>
      <Avatar className={cn(featured ? "w-14 h-14" : "w-10 h-10", "shrink-0")}>
        <AvatarImage
          src={employee?.photoUrl || ""}
          alt={`${executive.firstName} ${executive.lastName}`}
        />
        <AvatarFallback
          className={cn(featured ? "text-lg" : "text-sm", "font-semibold")}
        >
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-col min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              featured ? "text-base" : "text-sm",
              "font-semibold truncate",
            )}
          >
            {executive.firstName} {executive.lastName}
          </span>
          {employee?.linkedInUrl && (
            <a
              href={employee.linkedInUrl}
              target="_blank"
              rel="noreferrer"
              className="transition-colors text-muted-foreground hover:text-foreground shrink-0"
            >
              <RiLinkedinFill
                className={cn(featured ? "w-4 h-4" : "w-3.5 h-3.5")}
              />
            </a>
          )}
        </div>
        {executive.role && (
          <span
            className={cn(
              featured ? "text-sm" : "text-xs",
              "text-muted-foreground truncate",
            )}
          >
            {executive.role}
          </span>
        )}
      </div>
    </div>
  );
}

/** The key contact found for one CEO of one company, or none. */
type ExecutiveLookup = { key: string; employee: EmployeeDTO | null };

type CardCompanySummaryProps = {
  company: CompanyDTO;
  /** The founding year the web profile states, shown when the base has no creation date. */
  webFoundedYear?: CompanyWebFigure | null;
};

export default function CardCompanySummary({
  company,
  webFoundedYear,
}: CardCompanySummaryProps) {
  const { t } = useTranslation();
  const api = useApi();
  const arePeopleVisible = useArePeopleVisible();

  const directors = filterDirectors(company.business.executives);
  const ceoExecutive = findCompanyHead(directors);
  const otherExecutives = directors.filter((e) => e !== ceoExecutive);
  const hasExecutive = ceoExecutive !== null;
  const legalForm = displayableLegalForm(company.business.legalForm);

  // The directors come from the registry; their photo is looked up among the
  // key contacts, which a cut team does not see (#757, #840), by the name the
  // API lists them under (no name in parentheses). A string, so the lookup
  // runs again only when another CEO is named — the `legal` section can name
  // them in a patch after the card showed — not on every patch.
  const ceoName = ceoExecutive && directorUsualName(ceoExecutive);
  const ceoQuery =
    arePeopleVisible && ceoName
      ? `${ceoName.firstName} ${ceoName.lastName}`
      : null;
  const lookupKey = ceoQuery && `${company.id}|${ceoQuery}`;
  const [lookup, setLookup] = useState<ExecutiveLookup | null>(null);
  // Read at render, not reset in the effect: a card still mounted when the
  // team switches to a cut one, or when another CEO is named, must drop the
  // photo and LinkedIn it loaded for the previous one.
  const isExecutiveLoading = !!lookupKey && lookup?.key !== lookupKey;
  const visibleExecutiveEmployee =
    lookupKey && lookup?.key === lookupKey ? lookup.employee : null;

  // `api` is a new object on each render: read the latest one.
  const lookUpExecutive = useEffectEvent(
    async (key: string, query: string, signal: AbortSignal) => {
      // The registry writes "ELODIE", LinkedIn "Élodie".
      const normalize = (s: string) => toSearchableString(s).trim();
      let employee: EmployeeDTO | null = null;
      try {
        const { data } = await api.get<EmployeeSearchResponse>(
          `/company/${company.id}/employees?query=${encodeURIComponent(query)}&page=1&limit=1`,
          { signal },
        );
        const match = data.employees[0];
        if (
          match &&
          normalize(`${match.firstName} ${match.lastName}`) === normalize(query)
        ) {
          employee = match;
        }
      } catch {
        // No photo is not an error: the row shows the initials.
      }
      // The answer about a CEO a later patch replaced is dropped.
      if (!signal.aborted) setLookup({ key, employee });
    },
  );

  useEffect(() => {
    if (!lookupKey || !ceoQuery) return;
    const controller = new AbortController();
    void lookUpExecutive(lookupKey, ceoQuery, controller.signal);
    return () => controller.abort();
  }, [lookupKey, ceoQuery]);

  return (
    <Card className="flex flex-col min-w-0 overflow-hidden">
      {hasExecutive && (
        <>
          <CardContent className="flex flex-col gap-3 pt-6 pb-4">
            {isExecutiveLoading ? (
              <div className="flex flex-col gap-1.5 py-2">
                <Skeleton variant="subtle" className="w-24 h-2" />
                <Skeleton variant="subtle" className="w-16 h-1.5" />
              </div>
            ) : (
              <>
                <ExecutiveRow
                  executive={ceoExecutive}
                  employee={visibleExecutiveEmployee}
                  featured
                />
                {otherExecutives.length > 0 && (
                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium cursor-pointer text-muted-foreground hover:text-foreground transition-colors group">
                      <ChevronDownIcon className="w-3.5 h-3.5 transition-transform group-data-[state=open]:rotate-180" />
                      {t("company.insights.otherExecutives", {
                        count: otherExecutives.length,
                      })}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="flex flex-col gap-3 pt-3">
                        {otherExecutives.map((exec) => (
                          <ExecutiveRow
                            key={`${exec.firstName}-${exec.lastName}`}
                            executive={exec}
                          />
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </>
            )}
          </CardContent>
          <div className="mx-6 border-t" />
        </>
      )}

      <CardContent className={cn("flex flex-col", !hasExecutive && "pt-6")}>
        {company.business.creationDate && (
          <MetricRow
            icon={CalendarIcon}
            label={t("company.insights.creationDate")}
            value={new Date(company.business.creationDate).getFullYear()}
            showBorder={false}
          />
        )}
        {!company.business.creationDate && webFoundedYear && (
          <MetricRow
            icon={CalendarIcon}
            label={t("company.insights.creationDate")}
            value={
              <span className="flex flex-col items-end gap-0.5">
                {webFigureText(webFoundedYear)}
                <CompanyWebFigureNote figure={webFoundedYear} />
              </span>
            }
            showBorder={false}
          />
        )}

        {legalForm && (
          <MetricRow
            icon={ScaleIcon}
            label={t("company.insights.legalForm")}
            value={legalForm}
          />
        )}

        {company.business.enterpriseCategory && (
          <MetricRow
            icon={BuildingIcon}
            label={t("company.insights.enterpriseCategory")}
            value={
              ENTERPRISE_CATEGORY_FULL_NAMES[
                company.business.enterpriseCategory
              ] ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help">
                      {company.business.enterpriseCategory}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {
                      ENTERPRISE_CATEGORY_FULL_NAMES[
                        company.business.enterpriseCategory
                      ]
                    }
                  </TooltipContent>
                </Tooltip>
              ) : (
                company.business.enterpriseCategory
              )
            }
          />
        )}

        {company.stock?.isPubliclyTraded && company.stock.ticker && (
          <MetricRow
            icon={LineChartIcon}
            label={t("company.insights.stockTicker")}
            value={
              <>
                {company.stock.ticker}
                {company.stock.exchange && (
                  <span className="ml-1 font-normal text-muted-foreground">
                    ({company.stock.exchange})
                  </span>
                )}
              </>
            }
          />
        )}
      </CardContent>
    </Card>
  );
}
